import { useState } from 'react';
import { Stack, Button, Card, Modal, TextInput, Textarea, Select, Group, Badge, Text, Title, Accordion } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSpeakerphone } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { TablePagination } from '../components/TablePagination';
import { useClasses } from '../hooks/useLookups';
import { TableToolbar } from '../components/TableToolbar';
import { useDebouncedValue } from '@mantine/hooks';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { EmptyState } from '../components/EmptyState';
import { FetchOverlay } from '../components/FetchOverlay';
import { CardsSkeleton } from '../components/TableSkeleton';
import { formatDate } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';

interface Announcement {
  id: string;
  title: string;
  body: string;
  audience: string;
  linkScreen?: string | null;
  linkUrl?: string | null;
  createdAt: string;
  createdBy?: { fullName: string };
  class?: { id: string; name: string } | null;
}

export default function Announcements() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const AUDIENCE_LABELS: Record<string, string> = {
    ALL: t('bannersPage.audAll'), STAFF: t('nav.staff'), TEACHERS: t('bannersPage.audTeachers'),
    PARENTS: t('bannersPage.audParents'), STUDENTS: t('nav.students'), CLASS: t('fields.class'),
  };
  // Ilova ichidagi ochilishi mumkin bo'lgan ekranlar
  const APP_SCREENS: Record<string, string> = {
    Attendance: t('nav.attendance'), Grades: t('nav.grades'), Homework: t('nav.homework'), Payments: t('studentDetailPage.tabPayments'), Announcements: t('nav.announcements'),
  };
  const [opened, { open, close }] = useDisclosure(false);

  // Backend POST /announcements faqat STAFF∪TEACHING uchun ochiq
  const canPost = canAccess(user?.role, 'staff') || user?.role === 'TEACHER' || user?.role === 'HOMEROOM_TEACHER';

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [audF, setAudF] = useState<string>('');
  // Sinf filtri faqat "CLASS" auditoriyasi uchun ma'noli (backend `classId` ni o'qiydi)
  const [classF, setClassF] = useState<string>('');
  const [sort, setSort] = useState('createdAt:desc');
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi. */
  const filtrlar = {
    q: qDeb || undefined, audience: audF || undefined, classId: audF === 'CLASS' && classF ? classF : undefined, sort,
  };
  const { items: data, isLoading, fetching, pages, page, setPage, total, limit, setLimit} = usePagedQuery<Announcement>('announcements', '/announcements', filtrlar, 10);
  const { data: classes } = useClasses();
  const anyFilter = !!(qDeb || audF);

  const form = useForm({
    initialValues: { title: '', body: '', titleRu: '', bodyRu: '', titleEn: '', bodyEn: '', audience: 'ALL', classId: '', linkType: 'none', linkScreen: '', linkUrl: '' },
    validate: {
      title: (v) => (!v ? t('homeworkPage.titleRequired') : null),
      body: (v) => (!v ? t('announcementsPage.bodyRequired') : null),
      classId: (v, values) => (values.audience === 'CLASS' && !v ? t('homeworkPage.selectClass') : null),
      linkScreen: (v, values) => (values.linkType === 'app' && !v ? t('announcementsPage.selectScreen') : null),
      linkUrl: (v, values) => (values.linkType === 'url' && !/^https?:\/\//.test(v) ? t('announcementsPage.fullUrlRequired') : null),
    },
  });

  const isClassAudience = form.values.audience === 'CLASS';

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) =>
      api.post('/announcements', {
        title: v.title,
        body: v.body,
        titleRu: v.titleRu || undefined,
        bodyRu: v.bodyRu || undefined,
        titleEn: v.titleEn || undefined,
        bodyEn: v.bodyEn || undefined,
        audience: v.audience,
        classId: v.audience === 'CLASS' ? v.classId : undefined,
        linkScreen: v.linkType === 'app' ? v.linkScreen : undefined,
        linkUrl: v.linkType === 'url' ? v.linkUrl : undefined,
      }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('announcementsPage.posted') }); qc.invalidateQueries({ queryKey: ['announcements'] }); form.reset(); close(); },
  });

  return (
    <>
      <PageHeader title={t('pages.announcements.title')} action={canPost ? <Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.announcements.add')}</Button> : undefined} />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('announcementsPage.searchPlaceholder')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'createdAt:desc', label: t('announcementsPage.sortNew') },
          { value: 'title:asc', label: t('homeworkPage.sortTitleAsc') },
          { value: 'audience:asc', label: t('bannersPage.audience') },
        ]}
      >
        <Select
          placeholder={t('announcementsPage.audienceTo')}
          data={Object.entries(AUDIENCE_LABELS).map(([value, label]) => ({ value, label }))}
          value={audF || null}
          // Auditoriya CLASS'dan boshqasiga o'tsa — sinf filtri ham tozalanadi
          onChange={(v) => { setAudF(v ?? ''); if (v !== 'CLASS') setClassF(''); setPage(1); }}
          clearable w={150}
        />
        {audF === 'CLASS' && (
          <Select
            placeholder={t('announcementsPage.class')}
            data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={classF || null}
            onChange={(v) => { setClassF(v ?? ''); setPage(1); }}
            clearable searchable w={150}
          />
        )}
        <TableExport<Announcement>
          endpoint="/announcements"
          params={filtrlar}
          filename={t('nav.announcements')}
          ustunlar={[
            { key: 'title', label: t('fields.title') },
            { key: 'createdAt', label: t('fields.date') },
          ]}
          sanaMaydoni={(r) => r.createdAt}
        />
      </TableToolbar>
      {/* Karta doim ko'rinadi — ichida yuklanish/bo'sh holat/ro'yxat almashadi */}
      <SectionCard title={t('common.list')} icon={IconSpeakerphone} pos="relative">
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <Stack><CardsSkeleton count={4} height={96} /></Stack>
        ) : !data.length ? (
          <EmptyState icon={IconSpeakerphone} title={anyFilter ? t('common.noMatch') : t('pages.announcements.empty')} description="" />
        ) : (
          <Stack>
            {data.map((a) => (
              <Card key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/announcements/${a.id}`)}>
                <Group justify="space-between" mb={4}>
                  <Title order={5}>{a.title}</Title>
                  <Badge variant="light">
                    {a.audience === 'CLASS' && a.class ? t('announcementsPage.classNamed', { name: a.class.name }) : AUDIENCE_LABELS[a.audience] ?? a.audience}
                  </Badge>
                </Group>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{a.body}</Text>
                {a.linkUrl ? (
                  <Text size="xs" mt={4}><a href={a.linkUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{a.linkUrl}</a></Text>
                ) : a.linkScreen ? (
                  <Badge mt={4} size="sm" variant="dot">{t('announcementsPage.appNamed', { screen: APP_SCREENS[a.linkScreen] ?? a.linkScreen })}</Badge>
                ) : null}
                <Text size="xs" c="dimmed" mt="xs">{a.createdBy?.fullName} · {formatDate(a.createdAt)}</Text>
              </Card>
            ))}
          </Stack>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={close} title={t('pages.announcements.add')}>
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            <TextInput label={t('announcementsPage.titleUz')} required {...form.getInputProps('title')} />
            <Textarea label={t('announcementsPage.bodyUz')} minRows={3} autosize required {...form.getInputProps('body')} />
            <Accordion variant="contained">
              <Accordion.Item value="translations">
                <Accordion.Control>{t('announcementsPage.translations')}</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    <TextInput label={t('announcementsPage.titleRu')} {...form.getInputProps('titleRu')} />
                    <Textarea label={t('announcementsPage.bodyRu')} minRows={2} autosize {...form.getInputProps('bodyRu')} />
                    <TextInput label={t('announcementsPage.titleEn')} {...form.getInputProps('titleEn')} />
                    <Textarea label={t('announcementsPage.bodyEn')} minRows={2} autosize {...form.getInputProps('bodyEn')} />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
            <Select
              label={t('announcementsPage.audienceTo')}
              data={Object.entries(AUDIENCE_LABELS).map(([value, label]) => ({ value, label }))}
              allowDeselect={false}
              {...form.getInputProps('audience')}
              onChange={(v) => {
                form.setFieldValue('audience', v ?? 'ALL');
                if (v !== 'CLASS') form.setFieldValue('classId', '');
              }}
            />
            {isClassAudience && (
              <Select
                label={t('announcementsPage.whichClass')}
                placeholder={t('homeworkPage.selectClass')}
                data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
                searchable
                required
                {...form.getInputProps('classId')}
              />
            )}
            <Select
              label={t('announcementsPage.linkOptional')}
              data={[
                { value: 'none', label: t('announcementsPage.linkNone') },
                { value: 'app', label: t('announcementsPage.linkApp') },
                { value: 'url', label: t('announcementsPage.linkUrl') },
              ]}
              allowDeselect={false}
              {...form.getInputProps('linkType')}
            />
            {form.values.linkType === 'app' && (
              <Select
                label={t('announcementsPage.appScreenLabel')}
                placeholder={t('announcementsPage.selectScreenPlaceholder')}
                data={Object.entries(APP_SCREENS).map(([value, label]) => ({ value, label }))}
                required
                {...form.getInputProps('linkScreen')}
              />
            )}
            {form.values.linkType === 'url' && (
              <TextInput label={t('announcementsPage.url')} placeholder="https://..." required {...form.getInputProps('linkUrl')} />
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('announcementsPage.post')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
